import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ChartDataPoint {
  id: string;
  name: string;
  value: number;
  x?: number;
  y?: number;
  date?: string;
}

export interface CustomChart {
  id: string;
  title: string;
  type: 'line' | 'area' | 'bar' | 'pie' | 'scatter';
  data: ChartDataPoint[];
}

interface ChartsState {
  charts: CustomChart[];
}

const initialState: ChartsState = {
  charts: [
    {
      id: 'user-registrations',
      title: 'User Registrations Over Time',
      type: 'line',
      data: [
        { id: '1', name: 'Jan', value: 120, date: '2024-01' },
        { id: '2', name: 'Feb', value: 150, date: '2024-02' },
        { id: '3', name: 'Mar', value: 200, date: '2024-03' },
        { id: '4', name: 'Apr', value: 180, date: '2024-04' },
        { id: '5', name: 'May', value: 250, date: '2024-05' },
      ],
    },
    {
      id: 'system-performance',
      title: 'System Performance Metrics',
      type: 'area',
      data: [
        { id: '1', name: 'Week 1', value: 85, date: '2024-01-01' },
        { id: '2', name: 'Week 2', value: 88, date: '2024-01-08' },
        { id: '3', name: 'Week 3', value: 92, date: '2024-01-15' },
        { id: '4', name: 'Week 4', value: 89, date: '2024-01-22' },
        { id: '5', name: 'Week 5', value: 95, date: '2024-01-29' },
      ],
    },
    {
      id: 'department-activity',
      title: 'Department Activity Levels',
      type: 'bar',
      data: [
        { id: '1', name: 'Administration', value: 92 },
        { id: '2', name: 'Customer Support', value: 78 },
        { id: '3', name: 'Technical Team', value: 95 },
        { id: '4', name: 'Management', value: 85 },
        { id: '5', name: 'Analytics', value: 88 },
      ],
    },
    {
      id: 'user-roles',
      title: 'User Role Distribution',
      type: 'pie',
      data: [
        { id: '1', name: 'Admin', value: 15 },
        { id: '2', name: 'Manager', value: 25 },
        { id: '3', name: 'User', value: 45 },
        { id: '4', name: 'Guest', value: 15 },
      ],
    },
    {
      id: 'cost-vs-efficiency',
      title: 'Cost vs Efficiency Analysis',
      type: 'scatter',
      data: [
        { id: '1', name: 'Server A', x: 65, y: 120, value: 65 },
        { id: '2', name: 'Server B', x: 80, y: 95, value: 80 },
        { id: '3', name: 'Server C', x: 45, y: 150, value: 45 },
        { id: '4', name: 'Server D', x: 90, y: 80, value: 90 },
        { id: '5', name: 'Server E', x: 75, y: 110, value: 75 },
      ],
    },
  ],
};

const chartsSlice = createSlice({
  name: 'charts',
  initialState,
  reducers: {
    addDataPoint: (state, action: PayloadAction<{ chartId: string; dataPoint: Omit<ChartDataPoint, 'id'> }>) => {
      const chart = state.charts.find(c => c.id === action.payload.chartId);
      if (chart) {
        const newDataPoint = {
          ...action.payload.dataPoint,
          id: Date.now().toString(),
        };
        chart.data.push(newDataPoint);
      }
    },
    updateDataPoint: (state, action: PayloadAction<{ chartId: string; dataPointId: string; updates: Partial<ChartDataPoint> }>) => {
      const chart = state.charts.find(c => c.id === action.payload.chartId);
      if (chart) {
        const dataPoint = chart.data.find(d => d.id === action.payload.dataPointId);
        if (dataPoint) {
          Object.assign(dataPoint, action.payload.updates);
        }
      }
    },
    deleteDataPoint: (state, action: PayloadAction<{ chartId: string; dataPointId: string }>) => {
      const chart = state.charts.find(c => c.id === action.payload.chartId);
      if (chart) {
        chart.data = chart.data.filter(d => d.id !== action.payload.dataPointId);
      }
    },
    updateChartTitle: (state, action: PayloadAction<{ chartId: string; title: string }>) => {
      const chart = state.charts.find(c => c.id === action.payload.chartId);
      if (chart) {
        chart.title = action.payload.title;
      }
    },
    addChart: (state, action: PayloadAction<Omit<CustomChart, 'id'>>) => {
      const newChart = {
        ...action.payload,
        id: Date.now().toString(),
      };
      state.charts.push(newChart);
    },
    deleteChart: (state, action: PayloadAction<string>) => {
      state.charts = state.charts.filter(c => c.id !== action.payload);
    },
  },
});

export const {
  addDataPoint,
  updateDataPoint,
  deleteDataPoint,
  updateChartTitle,
  addChart,
  deleteChart,
} = chartsSlice.actions;

export default chartsSlice.reducer; 